import React, { useState } from "react";
import styled from "styled-components";
import { PostAdd } from "@material-ui/icons";
import { omit } from "lodash";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { TextArea } from "@sonder/ui/source/styled/fragments/TextArea";
import { DropdownContentAlignment } from "@sonder/ui/source/styled/surfaces/DropdownContent";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useCreateSavedFilter } from "core/queries/CreateSavedFilter";
import { useStore } from "tasks/store";
import { FilterOwner } from "typings/globalGQLTypes";

const StyledDropdownContent = styled(DropdownContent)`
  flex-direction: column;
  min-width: 20rem;
  padding: 1rem;

  h5 + ${TextArea}, ${TextArea} + ${TextArea}, ${TextArea} + div {
    margin-top: 1rem;
  }
`;

const StyledTitle = styled.h5`
  margin: 0;
`;

const StyledName = styled(TextArea)`
  border-bottom: 1px dashed ${colors.grayE};
  padding-bottom: 0.3rem;
  &:hover {
    border-bottom: 1px dashed ${colors.gray6};
  }
`;

const StyledDescription = styled(TextArea)`
  min-height: 4rem;
  overflow: auto;
`;

const StyledError = styled.p`
  color: ${colors.redD};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

interface CreateSavedFilterProps {
  alignContent?: DropdownContentAlignment;
  eventName?: string;
}

export const CreateSavedFilter = ({
  alignContent = "right",
  eventName = "create_saved_filter",
}: CreateSavedFilterProps) => {
  const errorReporter = useErrorReporterFiltered();
  const { track } = useSegment();
  const {
    state: { filters: globalFilters },
  } = useStore();
  const { state: authState } = useAuthStore();
  const cityId = authState?.user?.cityId || "";
  const isDispatchDashboardAdmin =
    authState?.user?.isDispatchDashboardAdmin || false;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [onCreateSavedFilter, { loading, error }] = useCreateSavedFilter({
    refetchQueries: ["GetSavedFilters"],
  });
  if (!isDispatchDashboardAdmin) {
    return null;
  }
  const trimmedName = name && name.trim();
  return (
    <Dropdown
      trigger={
        <CircleButton selected backgroundVariant="light">
          <PostAdd />
        </CircleButton>
      }
      onClose={() => {
        track({
          eventName,
          action: "click",
          element: "create_saved_filter.dropdown_trigger",
          props: { nextValue: "closed" },
        });
      }}
      onOpen={() => {
        track({
          eventName,
          action: "click",
          element: "create_saved_filter.dropdown_trigger",
          props: { nextValue: "opened" },
        });
      }}
    >
      {({ isOpen: isPortalOpen }) => {
        return (
          isPortalOpen && (
            <StyledDropdownContent align={alignContent}>
              <StyledTitle>New Filter</StyledTitle>
              <StyledName
                autoFocus
                required
                maxRows={2}
                placeholder="Add a name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
              <StyledDescription
                maxRows={14}
                placeholder="Add a description (optional)"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
              {error && error.message && (
                <StyledError>{error.message}</StyledError>
              )}
              <Footer>
                <PrimaryButton
                  disabled={!trimmedName || trimmedName === "" || loading}
                  onClick={async () => {
                    track({
                      eventName,
                      action: "click",
                      element: "create_saved_filter.create_filter",
                      props: { nextValue: trimmedName },
                    });
                    try {
                      const response = await onCreateSavedFilter({
                        variables: {
                          name,
                          description,
                          ownerId: cityId,
                          ownerType: FilterOwner.city,
                          filter: omit(globalFilters, [
                            "cleaningRequestCategory",
                          ]),
                        },
                      });
                      const newSavedFilterId =
                        response?.data?.createSavedTomFilter?.newSavedFilter
                          ?.id;
                      if (newSavedFilterId) {
                        track({
                          eventName,
                          action: "success",
                          element: "create_saved_filter.create_filter",
                          props: { nextValue: newSavedFilterId },
                        });
                      }
                      setName("");
                      setDescription("");
                    } catch (e) {
                      errorReporter.notify(e);
                    }
                  }}
                >
                  {loading ? "Saving" : "Save"}
                </PrimaryButton>
              </Footer>
            </StyledDropdownContent>
          )
        );
      }}
    </Dropdown>
  );
};
